import React from "react";

export default function StepsSection() {
  const steps = [
    {
      number: "01",
      title: "Enter Your Information",
      description: "Fill in your personal details, family information, and contact details in our user-friendly form. Add your photo and customize additional fields as needed.",
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200"
    },
    {
      number: "02", 
      title: "Choose Your Template",
      description: "Select from our collection of beautifully designed templates. Each template is crafted to make your biodata stand out with elegant layouts and modern designs.",
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
        </svg>
      ),
      color: "from-purple-500 to-pink-500",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200"
    },
    {
      number: "03",
      title: "Download Your PDF",
      description: "Generate and download your personalized biodata in high-quality PDF format. Your biodata is ready to share with family and friends.",
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      color: "from-green-500 to-emerald-500",
      bgColor: "bg-green-50",
      borderColor: "border-green-200"
    }
  ];

  return (
    <section 
      className="py-20 bg-gradient-to-br from-gray-50 to-blue-50"
      aria-label="How It Works"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 abc-bold">
            Create Your Perfect Biodata in Just 3 Easy Steps
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto abc-regular">
            Our streamlined process makes it simple to create a beautiful, personalized biodata in minutes
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <div
              key={step.number}
              className="relative group"
            >
              {/* Step Card */}
              <div className={`${step.bgColor} border-2 ${step.borderColor} rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative overflow-hidden`}>
                {/* Background Pattern */}
                <div className="absolute top-0 right-0 w-24 h-24 opacity-10">
                  <div className={`w-full h-full bg-gradient-to-br ${step.color} rounded-full blur-xl`}></div>
                </div>
                
                {/* Step Number */}
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${step.color} text-white text-2xl font-bold mb-6 abc-bold shadow-lg`}>
                  {step.number}
                </div>

                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-r ${step.color} text-white mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  {step.icon}
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-4 abc-semibold">
                  {step.title}
                </h3>
                <p className="text-gray-600 leading-relaxed abc-regular">
                  {step.description}
                </p>

                {/* Progress Indicator */}
                <div className="mt-6 flex items-center">
                  <div className={`w-full h-2 bg-gray-200 rounded-full overflow-hidden`}>
                    <div className={`h-full bg-gradient-to-r ${step.color} rounded-full transition-all duration-1000 group-hover:w-full`} style={{width: '0%'}}></div>
                  </div>
                </div>
              </div>

              {/* Enhanced Connector Line (except for last step) */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-6 lg:-right-12 transform -translate-y-1/2 z-10">
                  <div className="relative">
                    <div className="w-12 lg:w-24 h-1 bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300 rounded-full"></div>
                    <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-4 h-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-lg animate-pulse"></div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <button
            onClick={() => {
              document
                .getElementById("create-biodata")
                .scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-purple-300 abc-semibold"
            aria-label="Start Creating Your Biodata"
          >
            <span className="mr-2">🚀</span>
            Start Creating Your Biodata Now
            <span className="ml-2">→</span>
          </button>
        </div>
      </div>
    </section>
  );
}
